import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import LogoHolder from "./components/LogoHolder";
import Navigation from "./components/Navigation";
import MenuModal from "./components/MenuModal";
import "./Header.css";
import LanguagePicker from "./components/LanguagePicker";
import { hasMultilanguage } from "../../multilanguage";
import Search from "../Search/Search";

/**
 * Header component with responsive navigation and mobile menu.
 * Automatically dismisses mobile menu on route changes.
 * @param {Object} props - Component props
 * @param {string} props.mainColor - Primary text color
 * @param {string} props.secondaryColor - Hover/accent color
 * @param {string} props.bgColor - Background color
 * @param {string} props.logoSrc - Logo image source
 * @param {Array} props.menu - Navigation menu items
 * @param {boolean} props.isMobile - Whether in mobile view
 * @param {string} props.mobileMenuWidth - Width of mobile menu
 */
function Header({ mainColor, secondaryColor, bgColor, logoSrc, menu, isMobile, mobileMenuWidth }) {
  const [headerVisible, setHeaderVisible] = useState(true);
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuActive(false);
  }, [location.pathname]);

  let prevScrollY = useRef(window.scrollY);
  const handleScroll = useCallback(() => {
    const currentScrollPos = window.scrollY;
    if (!mobileMenuActive) {
      setHeaderVisible(currentScrollPos <= 150 || currentScrollPos <= prevScrollY.current);
    }
    prevScrollY.current = currentScrollPos;
  }, [mobileMenuActive]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuActive ? "hidden" : "auto";
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [mobileMenuActive, handleScroll]);

  function handleOnClick() {
    setMobileMenuActive(!mobileMenuActive);
  }

  return (
    <header id="header" className="header" style={{ top: headerVisible ? 0 : -154 }}>
      <Search />
      <div id="header-inner" className="header-inner" style={{ backgroundColor: bgColor }}>
        <div id="header-inner-container" className="header-inner-container">
          <div id="header-inner-logo" className="header-inner-logo">
            <LogoHolder src={logoSrc} color={mainColor} hoverColor={secondaryColor} />
          </div>
          <div id="header-inner-navigation" className="header-inner-navigation">
            <Navigation
              navItems={menu}
              color={mainColor}
              hoverColor={secondaryColor}
              isMobile={isMobile}
              bgColor={bgColor}
              mobileMenuActive={mobileMenuActive}
              onClick={handleOnClick}
            />
            {hasMultilanguage && <LanguagePicker />}
          </div>
        </div>
      </div>
      {isMobile && (
        <MenuModal
          active={mobileMenuActive}
          navItems={menu}
          color={mainColor}
          hoverColor={secondaryColor}
          bgColor={bgColor}
          mobileMenuWidth={mobileMenuWidth}
        />
      )}
    </header>
  );
}

export default Header;
